import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { TaskDataTableDocument } from 'generated/resources/datatable.graphql'
import { CreateOneOnOneTaskDocument } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface FormValues {
  name: string
  content: string
  dueOn: string
}

interface TaskModalProps {
  onClose: () => void
  participantIds: number[]
  meetingId: number
}

const TaskModal = ({ onClose, participantIds, meetingId }: TaskModalProps) => {
  const { employee } = useCurrentSession()
  const [createTask] = useMutation(CreateOneOnOneTaskDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.createOneononesTask,
  })

  const [assigneeSelection, setAssigneeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const getAssigneeIds = (): number[] => {
    if (assigneeSelection.type === 'manual') {
      return assigneeSelection.ids
    }
    return []
  }

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      content: '',
      dueOn: '',
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}
      if (!data.dueOn) {
        errors.dueOn = i18n.t('common.empty')
      }
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const assigneeIds = getAssigneeIds()
      if (assigneeIds.length === 0) {
        return { success: false, errors: [] }
      }

      const response = await createTask({
        variables: {
          meetingId: meetingId,
          participantId: employee?.id ?? 0,
          name: data.name,
          description: data.content,
          dueDate: dateUIToBackend(data.dueOn),
          assigneeIds: assigneeIds,
        },
        refetchQueries: [TaskDataTableDocument],
      })

      const result = handleResult(response, () => {
        // TODO: do something with form errors
      })

      if (result.success) {
        track(trackingEvents.one_on_ones.addOneToOneTask)
        onClose()
      }

      return result
    },
  })

  return (
    <Modal
      illustration="check"
      title={i18n.t('tasks.form.new.title')}
      description={i18n.t('tasks.form.new.description')}
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit()
      }}
      footer={
        <Button disabled={!form.isValid || form.isSubmitting || getAssigneeIds().length === 0} submit>
          {i18n.t('tasks.form.new.submit')}
        </Button>
      }
    >
      <FormRow>
        <TextField
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          label={i18n.t('tasks.labels.name')}
          mandatory
        />
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('tasks.labels.content')}
          </Text.Normal>
          <TextArea
            value={form.values.content}
            onChange={(value) => form.setValues({ ...form.values, content: value })}
            error={form.touched.content ? form.errors.content : undefined}
            placeholder="" // to remove default placeholder
            minRows={5}
          />
        </Box>
      </FormRow>
      <FormRow>
        <EmployeeSelectorV2
          multiple
          useAccessId
          label={i18n.t('tasks.labels.assignees')}
          mandatory
          forcedFilters={{ employeeIds: participantIds }}
          options={{ disableSelectAll: true }}
          onChange={setAssigneeSelection}
        />
      </FormRow>
      <FormRow>
        <DayPicker
          label={i18n.t('tasks.labels.due_date')}
          placeholder="–"
          value={form.values.dueOn}
          onChange={(date) => form.setValues({ ...form.values, dueOn: date })}
          error={form.touched.dueOn ? form.errors.dueOn : undefined}
        />
      </FormRow>
    </Modal>
  )
}

export default TaskModal
